import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { PanelGroup, Panel } from "react-resizable-panels";

import ResizeHandle from "components/common/ResizeHandle";
import MaximizeHost from "components/common/MaximizeHost";
import MaximizablePanel from "components/common/MaximizablePanel";
import AssetWellsSelector from "../components/AssetWellsSelector";
import WellsMap from "../components/WellsMap";
import WellsSelector from "./WellsSelector";
import ClusteringPanel from "./ClusteringPanel";
import AiTypeCurveChart from "./AITypeCurveChart";
import UserTypeCurveChart from "./UserTypeCurveChart";

import { ClusteringTypes } from "app/codes";

import {
  reset,
  setSelectedWellIds,
  selectSelectedWellIds,
  setProjectClusterDataItems,
  setProjectGroupDataItems,
  setMlInputs,
  setClusterWells,
  setGroupWells,
  selectClusterWells,
  selectGroupWells,
  selectDeclinePhase,
  selectClusteringType,
  setClusteringType,
} from "features/decline_analysis/typeCurveProjectSlice";

import {
  setShowNewProjectPopup,
  addProjectItem,
  setActiveProjectId,
  selectActiveProject,
} from "features/project/projectsSlice";

import { getProjectDetail, parseProjectId } from "service/projects";
import { saveProjectGroups, saveProjectClusters } from "service/typeCurve";
import useLoading from "components/common/useLoading";
import { Spin } from "antd";
import { PHASE_MODEL_MAP, DECLINE_PHASE_OPTIONS } from "app/codes";

function TypeCurve(props) {
  let [searchParams] = useSearchParams();
  const projectId = parseProjectId(searchParams.get("project_id"));

  const currentProject = useSelector(selectActiveProject);

  const selectedWellIds = useSelector(selectSelectedWellIds);

  const declinePhase = useSelector(selectDeclinePhase);
  const product = declinePhase
    ? PHASE_MODEL_MAP[declinePhase]
    : PHASE_MODEL_MAP[DECLINE_PHASE_OPTIONS[0].value];

  const clusteringType = useSelector(selectClusteringType);

  // The 2nd selector only lists wells that belong to the active cluster / group,
  // so it is empty until clustering / grouping is run. When empty it just wastes
  // horizontal space, so it collapses and the map takes the full width.
  const clusterWells = useSelector(selectClusterWells);
  const groupWells = useSelector(selectGroupWells);
  const selectorWellIds =
    clusteringType === ClusteringTypes.AIClustering ? clusterWells : groupWells;
  const hasSelectorWells =
    Array.isArray(selectorWellIds) && selectorWellIds.length > 0;

  const [LoadingIcon, showLoadingIcon, hideLoadingIcon] = useLoading(
    <Spin size="large" />,
    50
  );

  const dispatch = useDispatch();

  const [projectLoadStatus, setProjectLoadStatus] = useState(null); // loading, loaded

  // TC-1: the map is demoted from the golden centre to a collapsible context
  // strip, so the merged type-curve chart can be the page HERO.
  const [mapOpen, setMapOpen] = useState(false);

  const updateProjectData = useCallback(
    (projectData) => {
      const projectClusterData = projectData["project_clusters"];
      const projectGroupData = projectData["project_groups"];

      const mlInputs = projectData["ml_inputs"];

      if (projectClusterData && projectClusterData.length > 0) {
        dispatch(setProjectClusterDataItems(projectClusterData));
        dispatch(setClusteringType(ClusteringTypes.AIClustering));
      }
      if (projectGroupData && projectGroupData.length > 0) {
        dispatch(setProjectGroupDataItems(projectGroupData));
        if (projectClusterData.length === 0)
          dispatch(setClusteringType(ClusteringTypes.UserGroupping));
      }

      if (mlInputs) {
        dispatch(setMlInputs(mlInputs));
      }

      if (projectData.cluster_wells) {
        dispatch(setClusterWells(projectData.cluster_wells));
      }

      if (projectData.group_wells) {
        dispatch(setGroupWells(projectData.group_wells));
      }
    },
    [dispatch]
  );

  const refreshData = useCallback(
    (projId) => {
      showLoadingIcon();
      setProjectLoadStatus("loading");
      getProjectDetail(
        projId,
        (result) => {
          setProjectLoadStatus("loaded");
          if (currentProject == null) {
            dispatch(
              addProjectItem({
                id: result.project_id,
                name: result.project_name,
                project_type: result.project_type,
                custom_data: result.custom_data,
              })
            );
            dispatch(setActiveProjectId(result.project_id));
          }
          updateProjectData(result);
          hideLoadingIcon();
        },
        (error) => {
          console.error(error);
          setProjectLoadStatus(null);
          hideLoadingIcon();
        }
      );
    },
    [
      dispatch,
      currentProject,
      hideLoadingIcon,
      showLoadingIcon,
      updateProjectData,
    ]
  );

  useEffect(() => {
    return () => {
      dispatch(reset());
      dispatch(setActiveProjectId());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      projectLoadStatus === "loading" ||
      projectLoadStatus === "loaded" ||
      (projectId == null && currentProject == null)
    )
      return;
    refreshData(projectId || currentProject.id);
  }, [projectLoadStatus, projectId, currentProject, refreshData]);

  function handleSubmitTcUserProject(projectParams) {
    if (currentProject === null) {
      dispatch(setShowNewProjectPopup(true));
      return;
    }

    let userProjectItem = projectParams.projectItem;
    let tempItem = { ...userProjectItem, project_id: currentProject.id };
    showLoadingIcon();
    saveProjectGroups(
      currentProject.id,
      [tempItem],
      (result) => {
        hideLoadingIcon();
      },
      (error) => {
        hideLoadingIcon();
      }
    );
  }

  function handleSubmitTcClusterProject(projectParams) {
    if (currentProject === null) {
      dispatch(setShowNewProjectPopup(true));
      return;
    }
    let clusterProjectItem = projectParams.projectItem;
    let tempItem = { ...clusterProjectItem, project_id: currentProject.id };
    showLoadingIcon();
    saveProjectClusters(
      currentProject.id,
      null,
      [tempItem],
      "update",
      (result) => {
        hideLoadingIcon();
      },
      (error) => {
        hideLoadingIcon();
      }
    );
  }

  const handleWellSelected = useCallback(
    (well) => {
      let wellId = well.uwi;
      let tempIds = null;

      if (!selectedWellIds) {
        tempIds = [wellId];
      } else if (selectedWellIds.includes(wellId)) {
        tempIds = selectedWellIds.filter((item) => item !== wellId);
      } else {
        tempIds = [...selectedWellIds, wellId];
      }

      dispatch(setSelectedWellIds(tempIds));
    },
    [dispatch, selectedWellIds]
  );

  const handleWellsSelected = useCallback(
    (wells) => {
      if (wells == null || wells.length === 0) {
        return;
      }
      let wellIds = wells.map((well) => {
        return well.uwi;
      });

      let tempIds = null;
      if (!selectedWellIds) {
        tempIds = wellIds;
      } else {
        let tempIdsSet = new Set([...selectedWellIds, ...wellIds]);
        tempIds = [...tempIdsSet];
      }

      dispatch(setSelectedWellIds(tempIds));
    },
    [dispatch, selectedWellIds]
  );

  return (
    <>
      <LoadingIcon />
      <div
        className="page-content d-flex flex-column hide-scrollbar"
        style={{ position: "relative" }}
      >
        {/* Window "maximize / restore" host — overlays the split layout and
            covers ONLY this .page-content (left nav + breadcrumb stay visible). */}
        <MaximizeHost>
        {/* UX-REWORK · user-resizable split layout (react-resizable-panels).
            Outer vertical group splits the demoted CONTEXT STRIP from the HERO;
            autoSaveId persists every divider drag. The old fixed vh heights
            (22vh selectors / 30vh map / 56vh hero) are gone — the user sets the
            proportions by dragging. */}
        <PanelGroup
          direction="vertical"
          autoSaveId="type-curve-v1"
          style={{ flexGrow: 1, minHeight: 0 }}
        >
          {/* CONTEXT STRIP — fixed-width selector(s) on the left; the map fills
              the rest of the width. */}
          <Panel id="tc-context" order={1} defaultSize={24} minSize={12}>
            <div className="d-flex flex-row h-100">
              {/* Selectors: fixed width. The cluster/group-wells navigator (2nd
                  column) renders ONLY once clustering / grouping has produced
                  wells, so before that the map takes the full width. */}
              <div
                className="d-flex flex-row h-100"
                style={{ flex: "0 0 auto" }}
              >
                <div
                  className="pe-1 h-100"
                  style={{ width: "8.875rem", flexShrink: 0 }}
                >
                  <AssetWellsSelector
                    noAutoSelectFirstWell={true}
                    selectedWellIds={selectedWellIds}
                    onWellSelected={handleWellSelected}
                    onWellsSelected={handleWellsSelected}
                    loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                  />
                </div>
                {hasSelectorWells && (
                  <div
                    className="pe-1 h-100"
                    style={{ width: "8.875rem", flexShrink: 0 }}
                  >
                    <WellsSelector
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  </div>
                )}
              </div>

              {/* Map: fills the remaining width. */}
              <div
                className="d-flex flex-column flex-grow-1 ps-1"
                style={{ minWidth: 0 }}
              >
                <div
                  className="d-flex flex-row mb-1"
                  style={{ flex: "0 0 auto" }}
                >
                  <span
                    className="btn-label header"
                    role="button"
                    onClick={() => setMapOpen((v) => !v)}
                    style={{ cursor: "pointer", marginRight: 8 }}
                    title="Show / hide the well-location map"
                  >
                    {mapOpen ? "▼ Map" : "▶ Map"}
                  </span>
                </div>
                {mapOpen && (
                  <MaximizablePanel
                    id="tc-map"
                    className="flex-grow-1"
                    buttonStyle={{ right: 38 }}
                  >
                    <WellsMap
                      product={product}
                      selectedWellIds={selectedWellIds}
                      onWellSelected={handleWellSelected}
                      onWellsSelected={handleWellsSelected}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  </MaximizablePanel>
                )}
              </div>
            </div>
          </Panel>

          <ResizeHandle direction="vertical" id="tc-v-handle" />

          {/* HERO REGION — merged type-curve chart | (draggable) | clustering. */}
          <Panel id="tc-hero" order={2} defaultSize={76} minSize={30}>
            <PanelGroup
              direction="horizontal"
              autoSaveId="tc-hero-h1"
              style={{ height: "100%" }}
            >
              <Panel id="tc-chart" order={1} defaultSize={68} minSize={40}>
                {/* The chart card header's right edge is fully occupied
                    (Normalized-By + phase selects), so the maximize button
                    sits at the top-LEFT over the (non-interactive) title. */}
                <MaximizablePanel
                  id="tc-chart"
                  className="h-100 pe-1"
                  style={{ minWidth: 0 }}
                  buttonStyle={{ left: 8, right: "auto" }}
                >
                  {clusteringType === ClusteringTypes.AIClustering && (
                    <AiTypeCurveChart
                      submitTCProject={handleSubmitTcClusterProject}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  )}
                  {clusteringType === ClusteringTypes.UserGroupping && (
                    <UserTypeCurveChart
                      submitTCProject={handleSubmitTcUserProject}
                      loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                    />
                  )}
                </MaximizablePanel>
              </Panel>

              <ResizeHandle direction="horizontal" id="tc-hero-hh" />

              {/* TC-3 · permanent clustering / grouping sidebar. */}
              <Panel
                id="tc-clustering"
                order={2}
                defaultSize={32}
                minSize={20}
                maxSize={50}
              >
                <MaximizablePanel
                  id="tc-cluster"
                  className="h-100"
                  style={{ minWidth: 0 }}
                  buttonStyle={{ right: 38 }}
                >
                  <ClusteringPanel
                    loadingIconActions={[showLoadingIcon, hideLoadingIcon]}
                  />
                </MaximizablePanel>
              </Panel>
            </PanelGroup>
          </Panel>
        </PanelGroup>
        </MaximizeHost>
      </div>
    </>
  );
}

export default TypeCurve;
